import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BodyCopy } from '../components/BodyCopy';
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
} from 'lola-framework-ui-test';
import { useStore } from '../store/globalState';
import { useGalleryStore } from '../gallery/galleryStore';
import {
  accentInkOnSurface,
  type BrandMeshColors,
} from '../utils/accentInkOnSurface';
import { useCashDepositStore } from '../store/useCashDepositStore';
import { APP_ROUTES, CASH_DEPOSIT_ROUTES } from '../constants';
import {
  cashDepositMockMerchants,
  type CashDepositMockMerchant,
} from '../gallery/cashDepositMock';
import { ScrollFadeIndicator } from '../components/ScrollFadeIndicator';
import { Navbar } from '../components/Navbar';

const MapPinIcon = ({ color, size = 16 }: { color: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" fill={color} />
  </svg>
);

const StoreLogo = ({
  store,
  name,
}: {
  store: CashDepositMockMerchant;
  name: string;
}) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span className="stores-card-fallback" aria-hidden>
        {name}
      </span>
    );
  }

  return (
    <img
      src={store.logo}
      alt={name}
      className="stores-card-logo"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Gallery-safe stores picker for the cash-deposit flow.
 *
 * View map header + left-aligned copy + bordered store cards + disclaimer
 * footer. Selecting a merchant pins it for CashStores and navigates there.
 */
export const StoresPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const partnerColors = useGalleryStore(
    (s) => s.rawTheme.colors as BrandMeshColors & { inactived?: string },
  );
  const inactiveColor = partnerColors.inactived;
  const selectedMerchantId = useCashDepositStore((s) => s.selectedMerchantId);
  const setSelectedMerchantId = useCashDepositStore(
    (s) => s.setSelectedMerchantId,
  );
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    backgroundIcon,
    textColorBtn,
    bodyCopy,
    footerColor,
    title,
  } = colorConfig;
  const accentSolid = accentInkOnSurface(partnerColors, background, bodyCopy);
  const navigate = useNavigate();
  const merchants = cashDepositMockMerchants;

  const themeVars = {
    '--store-accent-btn': backgroundBtn,
    '--store-accent-solid': accentSolid,
    '--store-card-border': inactiveColor ?? bodyCopy,
    '--store-card-surface': background,
  } as React.CSSProperties;

  // The barcode is minted for one specific chain, so the merchant must be an
  // explicit tap here — the CTA only confirms it.
  const selectMerchant = (merchantId: string) => {
    setSelectedMerchantId(
      merchantId === selectedMerchantId ? null : merchantId,
    );
  };

  return (
    <>
    <AuraLayout colorConfig={colorConfig} className="stores-section-page">
      <Layout.Header style={themeVars}>
        <Navbar
          title={t('stores.viewMap')}
          align="right"
          color={bodyCopy}
          onBackClick={() => navigate(APP_ROUTES.CASH_DEPOSIT_LANDING_PAGE)}
          onIconClick={() => navigate(APP_ROUTES.STORES_MAP_PAGE)}
          icon={<MapPinIcon color={backgroundIcon} />}
        />
      </Layout.Header>

      <Layout.Content className="stores-section" style={themeVars}>
        <section className="stores-copy">
          <GradientText as="h1" textColor={bodyCopy} className="stores-title">
            {t('stores.title')}
          </GradientText>
          <BodyCopy textColor={bodyCopy} className="stores-subtitle">
            {t('stores.subtitle')}
          </BodyCopy>
        </section>

        <section
          className="stores-list"
          role="listbox"
          aria-label={t('stores.merchantsLabel')}
        >
          <ScrollFadeIndicator color={bodyCopy} />
          {merchants.map((store) => {
            const selected = store.id === selectedMerchantId;
            return (
              <button
                key={store.id}
                type="button"
                role="option"
                aria-selected={selected}
                className={`stores-card${selected ? ' stores-card--selected' : ''}`}
                onClick={() => selectMerchant(store.id)}
              >
                <StoreLogo store={store} name={t(store.nameKey)} />
              </button>
            );
          })}
        </section>
      </Layout.Content>

      <Layout.Footer>
        <section className="valid-cards">
          <section className="valid-cards-container stores-disclaimer-block">
            <BodyCopy className="stores-disclaimer-line" textColor={footerColor}>
              {t('stores.disclaimerLine1')} {t('stores.disclaimerLine2')}{' '}
              <GradientText
                as="span"
                className="stores-terms-link"
                textColor={title}
                onClick={() => navigate(CASH_DEPOSIT_ROUTES.LEGAL)}
              >
                {t('stores.termsLink')}
              </GradientText>
              .
            </BodyCopy>
          </section>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={!selectedMerchantId}
              onClick={() => {
                if (selectedMerchantId) {
                  navigate(APP_ROUTES.CASH_STORES_PAGE);
                }
              }}
            >
              {t('stores.cta')}
            </Button>
          </section>
        </section>
      </Layout.Footer>
    </AuraLayout>
  </>
  );
});
